import React from 'react';
import { AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import {
    Button,
    DebouncedSearch,
    ButtonVariants,
    ButtonColors,
    ConfirmModal,
} from '@clockout-dev/design-elements';
import { FILTER_PARAM_KEYS } from '../reports.constants';
import styles from './styles/reports-filter-bar.module.scss';

/**
 * Unified top bar for the Reports tables (Figma node 5747:1189).
 *
 * Horizontal layout: `Search` takes the flexible space, followed by the active
 * tab's filter fields and, on small screens, an icon button that opens them in
 * a `ConfirmModal` instead:
 *  - desktop (`>1024px`): fields inline as chips
 *  - small screen (mobile or tablet, `≤1024px`): full-width `Modal`
 *
 * The bar owns no filter state and knows nothing about either tab's filter
 * model: the page hands it `renderFields({ isMobile })` for the active tab
 * (`ReportsFiltersFields` for Users, `ReportsTransactionsFilterFields` for
 * Transactions) plus that tab's apply / reset handlers. Inline fields apply on
 * change; the modal applies on its Filter button.
 *
 * `isSmallScreen` is derived in `ReportsController` from the `isMobile` /
 * `isTablet` flags maintained by `ResponsiveController`, and propagated
 * from `Reports` via props.
 *
 * The search box is uncontrolled — `DebouncedSearch` reads `?search=` once, on
 * mount — so it is keyed on `tabKey` and on `searchResetKey`: a tab's reset
 * clears the term from state and the URL and bumps the counter, which remounts
 * the box empty. Without that the chip and the fetch forget the term while the
 * input still shows it.
 *
 * @param {Object} props
 * @param {string} props.tabKey remounts the search box per tab
 * @param {number} [props.searchResetKey=0] bumped by the tab's reset; remounts the search box
 * @param {boolean} props.isSmallScreen
 * @param {(search: string) => void} props.onSearch
 * @param {({isMobile: boolean}) => import('react').ReactNode} props.renderFields
 * @param {() => Promise<void> | void} [props.applyFilters]
 * @param {() => Promise<void> | void} [props.resetFilters]
 * @param {{title: string, description: string}} props.modalCopy
 */
function ReportsFilterBar({
    tabKey,
    searchResetKey = 0,
    isSmallScreen,
    onSearch,
    renderFields,
    applyFilters,
    resetFilters,
    modalCopy,
}) {
    const [open, setOpen] = React.useState(false);

    const close = React.useCallback(() => setOpen(false), []);

    // `ConfirmModal` calls these from its built-in footer. "Filter" wins
    // the OK slot (applies the staged filter state); "Reset All" sits in
    // the cancel slot but overrides the click handler so it actually
    // resets — `cancelButtonProps.onClick` (used below) takes precedence
    // over the default cancel handler.
    const handleModalApply = React.useCallback(async () => {
        await applyFilters?.();
        close();
    }, [applyFilters, close]);

    const handleModalReset = React.useCallback(async () => {
        await resetFilters?.();
        close();
    }, [resetFilters, close]);

    return (
        <div className={styles.container}>
            <DebouncedSearch
                key={`${tabKey}-search-${searchResetKey}`}
                searchParamKey={FILTER_PARAM_KEYS.SEARCH}
                placeholder="Search by name, email, or ID..."
                onSearch={onSearch}
                debounceMs={300}
                classNames={{ root: styles.searchInput }}
            />

            {!isSmallScreen ? (
                <div className={styles.chips}>
                    {renderFields({ isMobile: false })}
                </div>
            ) : null}

            {isSmallScreen ? (
                <>
                    <Button
                        variant={ButtonVariants.DEFAULT}
                        color={ButtonColors.SECONDARY}
                        onClick={() => setOpen((prev) => !prev)}
                        className={styles.filterButton}
                        aria-label="Open advanced filters"
                    >
                        <AdjustmentsHorizontalIcon width={18} height={18} />
                    </Button>
                    <ConfirmModal
                        open={open}
                        title={modalCopy?.title}
                        description={modalCopy?.description}
                        okText="Filter"
                        cancelText="Reset All"
                        okButtonColor={ButtonColors.SECONDARY}
                        onCancel={close}
                        onOk={handleModalApply}
                        cancelButtonProps={{ onClick: handleModalReset }}
                        classNames={{body: styles.modalChips}}
                        destroyOnHidden
                    >
                        {renderFields({ isMobile: true })}
                    </ConfirmModal>
                </>
            ) : null}
        </div>
    );
}

export default ReportsFilterBar;
